import { useCallback, useEffect, useState } from "react";
import { api, coincide, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Send, Inbox, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Mensajes() {
  const { user } = useAuth();
  const [buzon, setBuzon] = useState("interno");
  const [hilos, setHilos] = useState([]);
  const [activo, setActivo] = useState(null);
  const [respuesta, setRespuesta] = useState("");
  const [nuevo, setNuevo] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [form, setForm] = useState({ asunto: "", cuerpo: "", destinatarios: [] });
  const [enviando, setEnviando] = useState(false);
  const [tipoDest, setTipoDest] = useState("staff");
  const [sedes, setSedes] = useState([]);
  const [sedeFiltro, setSedeFiltro] = useState("todas");
  const [busca, setBusca] = useState("");
  const puedeBorrar = ["admin", "franquiciado"].includes(user?.role);

  const load = useCallback(() => {
    api.get(`/mensajes?buzon=${buzon}`).then(({ data }) => {
      setHilos(data);
      setActivo((prev) => (prev ? data.find((h) => h.id === prev.id) || null : null));
    }).catch(() => {});
  }, [buzon]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/sedes").then(({ data }) => setSedes(data)).catch(() => {}); }, []);
  useEffect(() => {
    const sede = sedeFiltro === "todas" ? "" : sedeFiltro;
    api.get(`/mensajes/contactos?tipo=${tipoDest}&sede_id=${sede}`)
      .then(({ data }) => setContactos(data)).catch(() => setContactos([]));
  }, [tipoDest, sedeFiltro]);

  const abrir = async (hilo) => {
    setActivo(hilo);
    if (hilo.no_leido) {
      await api.post(`/mensajes/${hilo.id}/leido`).catch(() => {});
      load();
    }
  };

  const crear = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post("/mensajes", form);
      toast.success(`Mensaje enviado · avisados: ${data.avisados.join(", ")}`);
      setForm({ asunto: "", cuerpo: "", destinatarios: [] });
      setNuevo(false);
      setBuzon(form.destinatarios.some((d) => d.tipo === "padre") ? "familias" : "interno");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setEnviando(false);
    }
  };

  const borrar = async (id) => {
    try {
      await api.delete(`/mensajes/${id}`);
      toast.success("Mensaje borrado");
      setActivo(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const alternar = (c) => setForm((f) => ({
    ...f,
    destinatarios: f.destinatarios.some((d) => d.id === c.id)
      ? f.destinatarios.filter((d) => d.id !== c.id)
      : [...f.destinatarios, { tipo: c.tipo || "staff", id: c.id, nombre: c.nombre, email: c.email }],
  }));

  const visibles = contactos.filter((c) => !busca.trim()
    || coincide(`${c.nombre} ${c.email} ${c.matricula || ""}`, busca));

  const responder = async () => {
    if (respuesta.trim().length < 1) return;
    try {
      await api.post(`/mensajes/${activo.id}/responder`, { cuerpo: respuesta });
      setRespuesta("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="mensajes-page">
      <PageHeader
        title="Buzón"
        subtitle="Bandeja interna del equipo y mensajes de las familias"
        action={
          <Button className="rounded-full font-semibold" onClick={() => setNuevo(true)} data-testid="nuevo-mensaje-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuevo mensaje
          </Button>
        }
      />

      <div className="flex gap-2 mb-6">
        {[["interno", "Interno (equipo)"], ["familias", "Familias"]].map(([k, label]) => (
          <button key={k} onClick={() => { setBuzon(k); setActivo(null); }}
            data-testid={`buzon-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
              buzon === k ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:bg-muted"
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          {hilos.map((h) => (
            <Card key={h.id} onClick={() => abrir(h)} data-testid={`hilo-${h.id}`}
              className={`rounded-2xl p-5 border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-[2px] ${
                activo?.id === h.id ? "border-primary bg-accent/40" : "border-border"
              }`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-display font-bold text-sm">{h.asunto}</p>
                {h.no_leido && <Badge className="rounded-full text-[10px]" data-testid={`hilo-nuevo-${h.id}`}>Nuevo</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {h.autor?.nombre} · {h.mensajes?.length || 1} mensaje(s)
              </p>
            </Card>
          ))}
          {hilos.length === 0 && (
            <Card className="rounded-2xl p-6 border-border" data-testid="buzon-vacio">
              <Inbox className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">
                {buzon === "interno"
                  ? "No tienes mensajes internos. Escribe al equipo cuando quieras."
                  : "No hay mensajes de familias pendientes."}
              </p>
            </Card>
          )}
        </div>

        <Card className="rounded-2xl p-6 border-border shadow-sm lg:col-span-2" data-testid="hilo-panel">
          {!activo && <p className="text-sm text-muted-foreground">Elige un mensaje para leerlo.</p>}
          {activo && (
            <>
              <h3 className="font-display text-xl font-bold">{activo.asunto}</h3>
              <div className="flex items-center justify-between gap-3 mt-1 mb-6">
                <p className="text-xs text-muted-foreground">
                  {(activo.destinatarios || []).map((d) => d.nombre).join(", ") || "Sin destinatarios"}
                </p>
                {puedeBorrar && (
                  <Button variant="outline" size="sm" className="rounded-full text-destructive"
                    onClick={() => borrar(activo.id)} data-testid="borrar-mensaje-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Borrar
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {(activo.mensajes || []).map((m, i) => (
                  <div key={i} className="rounded-xl border border-border p-4" data-testid={`mensaje-${i}`}>
                    <p className="text-xs font-semibold">{m.autor?.nombre}
                      <span className="text-muted-foreground font-normal"> · {(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </p>
                    <p className="text-sm mt-2 whitespace-pre-wrap">{m.cuerpo}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid gap-3">
                <Textarea value={respuesta} data-testid="respuesta-input"
                  placeholder="Escribe tu respuesta… usa @nombre para etiquetar"
                  onChange={(e) => setRespuesta(e.target.value)} />
                <Button className="rounded-full font-semibold justify-self-start" onClick={responder}
                  data-testid="responder-btn">
                  <Send className="h-4 w-4 mr-2" /> Responder
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      <Dialog open={nuevo} onOpenChange={setNuevo}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="nuevo-mensaje-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Nuevo mensaje</DialogTitle>
            <DialogDescription>
              Elige a quién va dirigido: recibirá un aviso por email.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Asunto</Label>
              <Input value={form.asunto} data-testid="mensaje-asunto-input"
                onChange={(e) => setForm({ ...form, asunto: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Para (obligatorio)</Label>
              <div className="flex gap-2">
                {[["staff", "Equipo"], ["familias", "Familias"]].map(([k, label]) => (
                  <button key={k} data-testid={`dest-tipo-${k}`}
                    onClick={() => { setTipoDest(k); setForm((f) => ({ ...f, destinatarios: [] })); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                      tipoDest === k ? "bg-primary text-primary-foreground border-primary"
                                     : "border-border text-muted-foreground hover:bg-muted"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              {tipoDest === "familias" && (
                <>
                  <Select value={sedeFiltro} onValueChange={setSedeFiltro}>
                    <SelectTrigger data-testid="dest-sede-select"><SelectValue placeholder="Sede" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas mis sedes</SelectItem>
                      {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={busca} placeholder="Buscar por alumno, tutor o matrícula…"
                    onChange={(e) => setBusca(e.target.value)} data-testid="dest-buscar-input" />
                  <Select value="" onValueChange={(id) => {
                    const c = contactos.find((x) => x.id === id);
                    if (c) alternar(c);
                  }}>
                    <SelectTrigger data-testid="dest-familia-select">
                      <SelectValue placeholder={`Elige alumno y tutor (${visibles.length})`} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[45vh]">
                      {visibles.map((c) => (
                        <SelectItem key={`${c.id}-${c.alumno}`} value={c.id}>
                          {c.nombre}{c.matricula ? ` · ${c.matricula}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2">
                    {form.destinatarios.map((d) => (
                      <button key={d.id} onClick={() => alternar(d)} data-testid={`destinatario-${d.id}`}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                        {d.nombre} ✕
                      </button>
                    ))}
                  </div>
                </>
              )}
              {tipoDest === "staff" && (
                <div className="flex flex-wrap gap-2">
                  {visibles.map((c) => (
                    <button key={c.id} onClick={() => alternar(c)} data-testid={`destinatario-${c.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                        form.destinatarios.some((d) => d.id === c.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:bg-muted"
                      }`}>
                      {c.nombre} · {c.rol}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid gap-2">
              <Label>Mensaje</Label>
              <Textarea value={form.cuerpo} rows={6} data-testid="mensaje-cuerpo-input"
                placeholder="Escribe aquí. También puedes etiquetar con @nombre o @email"
                onChange={(e) => setForm({ ...form, cuerpo: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                Recibirán un aviso por email los destinatarios elegidos y los etiquetados.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={crear} data-testid="mensaje-enviar-btn"
              disabled={enviando || form.asunto.trim().length < 2 || form.cuerpo.trim().length < 2
                || (form.destinatarios.length === 0 && !/@[\w.\-+]{2,}/.test(form.cuerpo))}>
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
